import { useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports -- NiceModal -> overlay-kit migration is out of scope for this PR
import NiceModal from '@ebay/nice-modal-react';
import { DehydratedState, Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import I18NextChainedBackend from 'i18next-chained-backend';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { appWithTranslation, UserConfig } from 'next-i18next';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { OverlayProvider } from 'overlay-kit';
import '../init';
import '../styles/globals.scss';
import '../styles/motions.scss';
import 'react-loading-skeleton/dist/skeleton.css';
import { defaultAxiosConfig, ZepApiProvider, ZepetofulProvider } from '@zep/apis';
import { AuthProvider } from '@zep/auth';
import { chainedBackendOptions } from '@zep/i18n';
import { Language, PageWithLayout, User } from '@zep/types';
import { isClient } from '@zep/utils';
import nextI18nConfig from '../../next-i18next.config';
import { getDefaultLayout } from '../components/layouts/DefaultLayout';
import RootLayout from '../components/layouts/RootLayout';
import Meta, { MetaProps } from '../components/service/common/Meta';
import { WebAppLoggerInitializer } from '../components/WebAppLoggerInitializer';
import GlobalErrorBoundary from '../guards/GlobalErrorBoundary';
import ChannelService, { ChannelTalkContext } from '../lib/ChannelTalk';
import { isDevelopment } from '../utils';

// design mode 전용 인스펙터. next/dynamic + ssr:false 로 별도 청크 분리해
// PRD 초기 번들에서 제외(렌더는 아래 NEXT_PUBLIC_STAGE 가드).
const DesignInspector = dynamic(() => import(/* webpackChunkName: "design-inspector" */'@zep/internal').then(m => ({
  default: m.DesignInspector
})), {
  ssr: false
});
const ZepApp = ({
  Component,
  pageProps
}: ZepAppProps) => {
  const {
    seoContent,
    serverUser
  } = pageProps;
  const router = useRouter();
  const locale = router.locale as Language ?? 'ko';
  const [channelTalk] = useState<ChannelService>(() => new ChannelService());
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        keepPreviousData: true,
        useErrorBoundary: true,
        retry: false,
        networkMode: 'always'
      },
      mutations: {
        networkMode: 'always'
      }
    }
  }));
  const getLayout = Component.getLayout ?? getDefaultLayout;
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_STAGE === 'LOC') return;
    const {
      hostname,
      pathname
    } = window.location;
    if (!hostname.endsWith('.zep.us') && hostname !== 'zep.us') {
      window.location.href = `https://zep.us${pathname}`;
    }
  }, []);
  return <>
      {process.env.NEXT_PUBLIC_STAGE !== 'PRD' && <DesignInspector />}
      <Meta partialSeoContent={seoContent} data-sentry-element="Meta" data-sentry-source-file="_app.tsx" />
      <GlobalErrorBoundary data-sentry-element="GlobalErrorBoundary" data-sentry-source-file="_app.tsx">
        <WebAppLoggerInitializer data-sentry-element="WebAppLoggerInitializer" data-sentry-source-file="_app.tsx" />
        <ChannelTalkContext.Provider value={channelTalk} data-sentry-element="unknown" data-sentry-source-file="_app.tsx">
          <NuqsAdapter data-sentry-element="NuqsAdapter" data-sentry-source-file="_app.tsx">
            <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
              <Hydrate state={pageProps.dehydratedState} data-sentry-element="Hydrate" data-sentry-source-file="_app.tsx">
                <ZepApiProvider axiosConfig={defaultAxiosConfig} data-sentry-element="ZepApiProvider" data-sentry-source-file="_app.tsx">
                  <ZepetofulProvider locale={locale} data-sentry-element="ZepetofulProvider" data-sentry-source-file="_app.tsx">
                    <AuthProvider initialUser={serverUser} data-sentry-element="AuthProvider" data-sentry-source-file="_app.tsx">
                      <RootLayout data-sentry-element="RootLayout" data-sentry-source-file="_app.tsx">
                        <NiceModal.Provider data-sentry-element="unknown" data-sentry-source-file="_app.tsx">
                          <OverlayProvider data-sentry-element="OverlayProvider" data-sentry-source-file="_app.tsx">
                            {getLayout(<Component {...pageProps} />)}
                          </OverlayProvider>
                        </NiceModal.Provider>
                      </RootLayout>
                      {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
                    </AuthProvider>
                  </ZepetofulProvider>
                </ZepApiProvider>
              </Hydrate>
            </QueryClientProvider>
          </NuqsAdapter>
        </ChannelTalkContext.Provider>
      </GlobalErrorBoundary>
    </>;
};
const clientI18nConfig: UserConfig = {
  ...nextI18nConfig,
  use: [I18NextChainedBackend],
  backend: chainedBackendOptions('web')
};
export default appWithTranslation(ZepApp, isClient() ? clientI18nConfig : nextI18nConfig);
type ZepAppProps = AppProps<{
  seoContent?: MetaProps['partialSeoContent'];
  serverUser?: User;
  dehydratedState?: DehydratedState;
}> & {
  Component: PageWithLayout;
};